import { Inject, Injectable, OnDestroy } from '@angular/core'
import { Subject, takeUntil } from 'rxjs'
import { MapService } from 'src/app/core/services/map.service'
import maplibregl, { LineLayerSpecification, SymbolLayerSpecification } from 'maplibre-gl'
import { emptyGeoJsonSource } from 'src/app/core/utils/source-utils'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { NavigationStart, Router } from '@angular/router'
import { MapEventsService } from 'src/app/core/services/map-events.service'
import { Layers } from '../layers-config/layers'
import { DataConstructionSitesService } from '../services/data-construction-sites.service'
import { MapIcons } from 'src/app/core/enums/map-icons.enum'

@Injectable()
export class LayerConstructionSitesService {
  private layer = this.layerPrefix
  public layerLinesId = `${this.layer}-lines`
  public layerSymbolsId = `${this.layer}-symbols`
  public layerConstructionSitesDirectionId = `${this.layer}-direction-symbols`

  public sourceLineId = `${this.layer}-line-source`
  public sourceSymbolsId = `${this.layer}-symbol-source`
  public sourceSymbolsCountId = `${this.layer}-symbol-count-source`

  public isLayerVisible = false
  private cleanUp$ = new Subject<void>()
  private timestamp: Date | undefined
  private timespan: number | undefined
  public selectedFeatureId: string = ''
  public selectedSegmentId: string = ''
  public layerInitialized: boolean = false
  public onDestroy: boolean = false
  public whileReload = false

  private constructionSitesSegmentLayerSpecification: LineLayerSpecification = {
    id: this.layerLinesId,
    type: 'line',
    minzoom: 8,
    source: this.sourceLineId,
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },

    filter: ['==', ['to-string', ['get', 'id']], this.selectedFeatureId || ''],
    paint: {
      'line-color': '#04781b',
      'line-width': [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        10,
        this.getSelectedFeatureExpression(this.selectedFeatureId),
        10,
        6,
      ],
    },
  }

  private constructionSitesDirectionLayerSpecification: SymbolLayerSpecification = {
    id: this.layerConstructionSitesDirectionId,
    source: this.sourceLineId,
    minzoom: this.minZoom,
    type: 'symbol',
    filter: ['==', ['to-string', ['get', 'id']], this.selectedFeatureId || ''],
    layout: {
      'symbol-sort-key': [
        'case',
        this.getSelectedFeatureExpression(this.selectedFeatureId),
        1000000000000, // High value to bring selected feature to front
        0,
      ],
      'symbol-placement': 'line',
      'symbol-spacing': 100,
      'icon-size': 1,
      'icon-image': [
        'case',
        ['==', ['get', 'direction'], 'BOTH'],
        MapIcons.ARROW_BOTH,
        MapIcons.ARROW_LEFT,
      ],
      'icon-rotate': ['case', ['==', ['get', 'direction'], 'AGAINST'], 0, 180],
      'icon-rotation-alignment': 'map',
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  }

  private constructionSitesLayerSpecification: any = {
    id: this.layerSymbolsId,
    source: this.sourceSymbolsId,
    minzoom: this.minZoom,
    type: 'circle',
    paint: {
      // Bright yellow fill
      'circle-color': '#04781b',
      // Black border for contrast
      'circle-stroke-color': '#222',
      'circle-stroke-width': 2,
      // Zoom-based sizing, highlight if selected
      'circle-radius': ['case', this.getSelectedFeatureExpression(this.selectedFeatureId), 8, 6],
    },
  }
  constructor(
    private router: Router,
    private readonly mapService: MapService,
    private readonly mapEventsService: MapEventsService,
    private appState: AppStateService,
    @Inject(String) private layerPrefix: string,
    @Inject(Number) private minZoomAvailable: number,
    @Inject(Number) private maxZoomAvailable: number,
    @Inject(Number) private minZoom: number,
    private dataService: DataConstructionSitesService,
  ) {
    this.selectedFeatureId = this.appState.getState().selectedFeatureId ?? ''
    if (mapService.isMapReady) {
      this.initializeLayer()
    } else {
      this.mapService.mapReadyChanged.subscribe(() => {
        this.initializeLayer()
      })
    }
    this.appState.stateChanged.pipe(takeUntil(this.cleanUp$)).subscribe((state) => {
      if (this.layerInitialized) {
        this.evaluateState()
      }
    })

    // NgOnDestroy is not working on instances created by a factory, therefore we need this workaround
    this.router.events.pipe(takeUntil(this.cleanUp$)).forEach((event) => {
      if (event instanceof NavigationStart) {
        this.ngOnDestroy()
      }
    })
  }

  private async initializeLayer() {
    this.mapService.map.addSource(this.sourceLineId, {
      ...emptyGeoJsonSource,
      promoteId: 'id',
    })

    this.mapService.map?.addSource(this.sourceSymbolsId, {
      ...emptyGeoJsonSource,
      promoteId: 'id',
    })

    this.mapService.map.addLayer(this.constructionSitesSegmentLayerSpecification)
    this.mapService.map?.addLayer(this.constructionSitesDirectionLayerSpecification)
    this.mapService.map?.addLayer(this.constructionSitesLayerSpecification)

    this.mapEventsService.registerForMapEvents({ layerId: this.layerSymbolsId }, true)

    this.layerInitialized = true

    // Immediately update the layer properties with the correct selectedFeatureId
    if (this.selectedFeatureId && this.selectedFeatureId !== '') {
      this.changeSelectAndHoverStateOfSelectedFeature()
    }
    this.evaluateState()
  }

  ngOnDestroy() {
    this.onDestroy = true

    this.mapService.map.removeLayer(this.layerLinesId)

    this.mapEventsService.detachMapEvents({ layerId: this.layerSymbolsId })
    this.mapEventsService.detachMapEvents({ layerId: this.layerLinesId })
    this.mapService.map.removeLayer(this.layerSymbolsId)
    this.mapService.map.removeLayer(this.layerConstructionSitesDirectionId)
    this.mapService.map.removeSource(this.sourceSymbolsId)
    this.mapService.map.removeSource(this.sourceLineId)

    // Cancel all requests and clean up properly
    this.cancelRequests()
    this.cleanUp$.next()
    this.cleanUp$.complete()
  }

  // Update your cancelRequests method to properly cancel HTTP requests
  private cancelRequests() {
    // Cancel requests in the data service
    this.dataService.cancelCurrentRequests()
  }

  // Update your reload method to handle cancellation gracefully
  public async reload() {
    // Cancel any previous requests first
    this.cancelRequests()

    if (this.mapService.isMapReady && this.mapService.map.getZoom() >= this.minZoom) {
      await this.dataService.loadConstructionSitesData(
        this.appState.isHistoryEnabled(),
        this.mapService.getExpandedBounds(0.5),
        this.appState.selectTimestamp(),
      )
      this.updateLayer()
    }
  }

  public async updateLayer() {
    if (this.mapService.isMapReady && this.whileReload == false) {
      const constructionSites = this.dataService.getConstructionSites()
      this.mapService.setGeoJsonData(this.sourceSymbolsId, constructionSites[0])
      this.mapService.setGeoJsonData(this.sourceLineId, constructionSites[1])
      this.changeSelectAndHoverStateOfSelectedFeature()
    }
  }

  private async evaluateState() {
    const layers = this.appState.selectLayers()
    if (layers.some((layer) => layer === Layers.ConstructionSites.name)) {
      this.setVisibility(true)
    } else {
      this.setVisibility(false)
    }
    // if layer is not visible stop all events
    if (this.isLayerVisible === false) {
      return
    }

    if (!this.timestamp) this.timestamp = this.appState.selectTimestamp()

    if (!this.dataService.isDataInitialized) {
      await this.reload()
    }

    if (
      this.appState.hasLayerVisibilityChanged(this.layer) &&
      !this.appState.getState().historyEnabled
    ) {
      await this.reload()
    }

    if (this.appState.hasMapBoundsChanged() && !this.appState.getState().historyEnabled) {
      await this.reload()
    }

    if (this.appState.hasTimestampChanged()) {
      this.timestamp = this.appState.selectTimestamp()
      await this.reload()
    }

    if (
      this.appState.hasSelectedFeatureIdChanged() &&
      this.appState.getState().selectedFeatureId !== ''
    ) {
      this.selectedFeatureId = this.appState.getState().selectedFeatureId ?? ''
      this.updateLayer()
      this.changeSelectAndHoverStateOfSelectedFeature()
    }
  }

  setVisibility(isVisible: boolean): void {
    this.isLayerVisible = isVisible
    if (this.mapService.isMapReady) {
      this.mapService.setLayerVisibility(this.layerSymbolsId, isVisible)
      this.mapService.setLayerVisibility(this.layerLinesId, isVisible)
      this.mapService.setLayerVisibility(this.layerConstructionSitesDirectionId, isVisible)
      if (!isVisible) {
        // Cancel any ongoing requests when layer becomes invisible
        this.cancelRequests()
      }
    }
  }

  private changeSelectAndHoverStateOfSelectedFeature() {
    if (this.onDestroy) return
    this.mapService.map.setFilter(this.layerLinesId, [
      '==',
      ['to-string', ['get', 'id']],
      this.selectedFeatureId || '',
    ])
    this.mapService.map.setFilter(this.layerConstructionSitesDirectionId, [
      '==',
      ['to-string', ['get', 'id']],
      this.selectedFeatureId || '',
    ])
    this.mapService.map.setPaintProperty(this.layerSymbolsId, 'circle-radius', [
      'case',
      ['boolean', ['feature-state', 'hover'], false],
      8,
      this.getSelectedFeatureExpression(this.selectedFeatureId),
      8,
      6,
    ])

    this.mapService.map.setPaintProperty(this.layerLinesId, 'line-width', [
      'case',
      ['boolean', ['feature-state', 'hover'], false],
      10,
      this.getSelectedFeatureExpression(this.selectedFeatureId),
      10,
      6,
    ])
  }

  private getSelectedFeatureExpression(
    selectedFeatureId: string,
  ): maplibregl.ExpressionSpecification {
    return ['any', ['==', ['to-string', ['get', 'id']], selectedFeatureId || '']]
  }
}
